import { E_EARN_VAULT_CHAIN_ID } from '@/config/euler-contracts'
import { getTokenImageUrl } from '@/domain/euler'
import { getDisplayTokenSymbol } from '@/domain/hooks/useDisplayToken'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { SectionHeaderTitle } from '@/ui/molecules/section-header/SectionHeader'
import { Sparkles } from 'lucide-react'
import { ArrowRight } from 'lucide-react'
import Image from 'next/image'
import type { EVaultData } from '../logic/useEVaults'
import { EarnPointsBadge } from './EarnPointsBadge'

function formatTVL(tvl: number | undefined): string {
  if (!tvl) return '$0'
  if (tvl >= 1_000_000_000) return `$${(tvl / 1_000_000_000).toFixed(2)}B`
  if (tvl >= 1_000_000) return `$${(tvl / 1_000_000).toFixed(2)}M`
  if (tvl >= 1_000) return `$${(tvl / 1_000).toFixed(2)}K`
  return `$${tvl.toFixed(2)}`
}

interface EarnVaultShowcaseProps {
  vaults: EVaultData[]
  onVaultClick: (vaultAddress: string) => void
}

export function EarnVaultShowcase({ vaults, onVaultClick }: EarnVaultShowcaseProps) {
  if (vaults.length === 0) return null
  const showcaseVaults = vaults.slice(0, 4)

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-1">
        <SectionHeaderTitle icon={<Sparkles className="h-4 w-4 text-[#FBE572]" />} title="Featured Vaults" />
        <span className="text-muted-foreground text-xs">
          {showcaseVaults.length} vault{showcaseVaults.length !== 1 ? 's' : ''} featured
        </span>
      </div>

      <div className="-mx-3 sm:mx-0">
        <div className="no-scrollbar flex gap-3 overflow-x-auto px-3 pb-2 sm:grid lg:grid-cols-4 sm:grid-cols-2 sm:gap-4 sm:overflow-visible sm:px-0 sm:pb-0">
          {showcaseVaults.map((vault, i) => (
            <div
              key={vault.address}
              className="w-[280px] shrink-0 sm:w-full"
              style={{
                animationDelay: `${i * 0.08}s`,
                animationName: 'sweep-card-in',
                animationDuration: '0.4s',
                animationTimingFunction: 'ease-out',
                animationFillMode: 'forwards',
                opacity: 0,
              }}
            >
              <ShowcaseCard vault={vault} onClick={() => onVaultClick(vault.address)} />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

interface ShowcaseCardProps {
  vault: EVaultData
  onClick: () => void
}

function ShowcaseCard({ vault, onClick }: ShowcaseCardProps) {
  const assetSymbol = vault.assetSymbol || vault.symbol
  const displaySymbol = getDisplayTokenSymbol(E_EARN_VAULT_CHAIN_ID, TokenSymbol(assetSymbol))
  const tokenImage = getTokenImageUrl(vault.underlyingTokenInfo, assetSymbol)

  return (
    <div
      onClick={onClick}
      className="group relative cursor-pointer rounded-xl bg-gradient-to-r from-gradient-yellow/20 to-gradient-green/20 p-[1px] transition-all sm:rounded-2xl hover:from-gradient-yellow/40 hover:to-gradient-green/40"
    >
      <div className="flex h-full min-h-[120px] flex-col justify-between rounded-xl bg-card p-3 sm:min-h-[140px] sm:rounded-2xl sm:p-5">
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 items-start gap-2 sm:gap-3">
            <Image
              src={tokenImage}
              alt={displaySymbol}
              width={28}
              height={28}
              className="h-7 w-7 shrink-0 rounded-full"
            />
            <div className="flex min-w-0 flex-col">
              <span className="truncate font-semibold text-base text-white sm:text-lg">{displaySymbol}</span>
              <span className="max-w-[120px] truncate text-muted-foreground text-xs">{vault.name}</span>
              <span className="mt-1 text-[11px] text-muted-foreground/80">{formatTVL(Number(vault.tvlUsd))} TVL</span>
            </div>
          </div>

          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground transition-transform sm:h-10 sm:w-10 group-hover:scale-110">
            <ArrowRight className="h-4 w-4 text-black sm:h-5 sm:w-5" />
          </div>
        </div>

        <div className="mt-3 flex items-end sm:mt-4">
          <div className="flex items-baseline gap-1">
            <Sparkles className="mb-0.5 h-3 w-3 text-gradient-yellow sm:mb-1 sm:h-4 sm:w-4" />
            <span className="bg-gradient-to-r from-gradient-yellow to-gradient-green bg-clip-text font-bold text-2xl text-transparent sm:text-4xl">
              {vault.supplyAPY !== undefined && vault.supplyAPY > 0 ? `${vault.supplyAPY.toFixed(2)}%` : '0.00%'}
            </span>
            <span className="ml-0.5 font-medium text-muted-foreground text-xs sm:ml-1 sm:text-sm">APY</span>
            <div className="ml-1">
              <EarnPointsBadge />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
